import { Pipe, PipeTransform } from '@angular/core';
import {
  ensureNumericPaymentType,
  isPaymentTypeOption,
  PaymentTypeOption,
} from 'src/app/core/interfaces/document.interface';
import { PaymentType, PaymentTypeTranslations, PaymentMethodsList } from 'src/app/core/interfaces/invoice.interface';
import { LocalizationService } from 'src/app/shared/services/localization.service';

/**
 * Maps PaymentTypeFilter string keys (used in POS) to their display labels.
 * Arabic labels match the POS Figma design: نقدي / آجل / شبكة
 */
const PAYMENT_FILTER_LABELS: Record<string, { ar: string; en: string }> = {
  Cash :  { ar: 'نقدي',  en: 'Cash'  },
  Debit : { ar: 'آجل',   en: 'Debit' },
  Mada  : { ar: 'شبكة',  en: 'Mada'  },
  MultiPayment : { ar: 'متعدد',  en: 'Multi Payment' },
};

function resolvePaymentTypeOptionLabel(
  option: PaymentTypeOption,
  currentLang: string,
): string {
  const nameAr = option.nameAr?.trim();
  const nameEn = option.nameEn?.trim();

  if (currentLang === 'ar' && nameAr) {
    return nameAr;
  }
  if (nameEn) {
    return nameEn;
  }
  if (nameAr) {
    return nameAr;
  }
  return '';
}

@Pipe({
    name: 'paymentTypeTranslator',
    pure: false
})
export class PaymentTypeTranslatorPipe implements PipeTransform {

    constructor(private localizationService: LocalizationService) { }

    transform(value: PaymentType | string | PaymentTypeOption | null | undefined): string {
        if (value === null || value === undefined || value === '') {
            return '';
        }

        const currentLang = this.localizationService.getCachedLanguage() ?? 'ar';

        if (isPaymentTypeOption(value)) {
            const optionLabel = resolvePaymentTypeOptionLabel(value, currentLang);
            if (optionLabel) {
                return optionLabel;
            }
            const rawValue = value.value ?? (value as { id?: number | string }).id;
            if (rawValue === null || rawValue === undefined || rawValue === '') {
                return '';
            }
            value = rawValue;
        }

        const stringValue = String(value);
        
        // Handle PaymentTypeFilter string keys: 'Cash', 'Debit', 'Mada'
        if (Object.prototype.hasOwnProperty.call(PAYMENT_FILTER_LABELS, stringValue)) {
            const label = PAYMENT_FILTER_LABELS[stringValue];
            return currentLang === 'ar' ? label.ar : label.en;
        }

        const normalized = ensureNumericPaymentType(value);
        const typeId = Number(normalized);
        if (!isNaN(typeId) && typeId > 0) {
            const item = PaymentMethodsList.find(m => m.id === typeId);
            if (item) {
                return currentLang === 'ar' ? item.nameAr : item.nameEn;
            }

            // Last resort: use PaymentTypeTranslations map (Arabic only)
            if (currentLang === 'ar') {
                const translated = PaymentTypeTranslations[typeId as PaymentType];
                if (translated) {
                    return translated;
                }
            }
        }
        return stringValue;
    }
}
